import React, { useCallback, useEffect, useMemo, useState } from 'react'
import ReactPaginate from 'react-paginate'
import { PageTitle } from '../../../../../../../_metronic/layout/core'
import { Table } from '../../../../../../../components/Table'
import { TableHead } from '../../../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../../../components/Table/TableBody'
import { LoaderForTable } from '../../../../../../../components/Loader'
import { FilterManager, IBaseFilters } from '../../../../../../common/filter/FilterManager'
import { ISystemSetting, ISettingsService } from '../../core/response.types'
import { ApplyFiltersArgs } from '../../core/types'
import { SystemSettingsListQuery } from '../../core/query.types'
import { DEFAULT_SYSTEM_SETTINGS_QUERY } from '../../core/query.defaults'
import { SYSTEM_SETTINGS_BREADCRUMBS } from '../../core/consts'
import { getSystemSettingsList, getSettingsServices } from '../../core/api'
import { extractSettingsError } from '../../core/errors'
import SystemSettingsRow from './SystemSettingsRow'
import ServiceSelector from './ServiceSelector'

const SETTINGS_UNAVAILABLE = 'Настройки сервиса недоступны или не реализованы'

export default function SystemSettingsList() {
  const [initialized, setInitialized] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [settings, setSettings] = useState<ISystemSetting[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [query, setQuery] = useState<SystemSettingsListQuery>(DEFAULT_SYSTEM_SETTINGS_QUERY)
  const [services, setServices] = useState<ISettingsService[]>([{ code: 'core', label: 'Core (Backend)' }])
  const [loadError, setLoadError] = useState<string | null>(null)

  const currentService = query.service ?? 'core'

  const loadSettings = useCallback(async (params: SystemSettingsListQuery) => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const res = await getSystemSettingsList(params)
      if (!res?.data?.success) {
        throw res
      }
      const list = res.data.data ?? []
      const total = res.data.totalRows ?? 0
      setSettings(list)
      setTotalRows(total)
      setPageCount(Math.ceil(total / params.perPage))
    } catch (e) {
      console.error('Ошибка загрузки настроек:', e)
      setSettings([])
      setTotalRows(0)
      setPageCount(0)
      setLoadError(extractSettingsError(e, SETTINGS_UNAVAILABLE))
    } finally {
      setIsLoading(false)
    }
  }, [])

  const applyFilters = useCallback(
    ({ patch, page }: ApplyFiltersArgs = {}) => {
      const nextQuery: SystemSettingsListQuery = {
        ...DEFAULT_SYSTEM_SETTINGS_QUERY,
        ...query,
        ...patch,
        page: page ?? patch?.page ?? query.page,
      }
      setQuery(nextQuery)
      loadSettings(nextQuery)
    },
    [query, loadSettings]
  )

  const handleFilterApply = useCallback(
    (filters: IBaseFilters) => {
      applyFilters({
        patch: {
          search: filters.search ?? undefined,
          page: 1,
        },
      })
    },
    [applyFilters]
  )

  const handleServiceChange = useCallback(
    (service: string) => {
      applyFilters({
        patch: {
          service,
          search: undefined,
          namespace: undefined,
          page: 1,
        },
      })
    },
    [applyFilters]
  )

  useEffect(() => {
    if (initialized) return
    applyFilters()
    setInitialized(true)
  }, [initialized, applyFilters])

  useEffect(() => {
    let cancelled = false
    getSettingsServices()
      .then((res) => {
        if (cancelled) return
        if (res?.data?.success && Array.isArray(res.data.data) && res.data.data.length > 0) {
          setServices(res.data.data)
        }
      })
      .catch((e) => console.error('Ошибка загрузки списка сервисов:', e))
    return () => {
      cancelled = true
    }
  }, [])

  // Группировка настроек по namespace
  const groupedSettings = useMemo(() => {
    const groups: Record<string, ISystemSetting[]> = {}
    for (const s of settings) {
      if (!groups[s.namespace]) groups[s.namespace] = []
      groups[s.namespace].push(s)
    }
    return groups
  }, [settings])

  return (
    <div>
      <PageTitle breadcrumbs={SYSTEM_SETTINGS_BREADCRUMBS}>Настройки системы</PageTitle>

      {/* Фильтры */}
      <FilterManager
        activeFilters={['search']}
        defaultValues={{ search: '' }}
        onApplyFilters={handleFilterApply}
      />

      {/* Карточка с таблицей */}
      <div className='card'>
        <div className='card-header border-0 pt-5 pb-2 d-flex justify-content-between align-items-center'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Настройки системы</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              Всего:{' '}
              {isLoading ? (
                <span className='spinner-border spinner-border-sm align-middle' />
              ) : (
                totalRows
              )}
            </span>
          </h3>

          {/* Селектор сервиса (переключатель источника, не фильтр) — в правом верхнем углу карточки */}
          <div className='card-toolbar'>
            <ServiceSelector
              services={services}
              value={currentService}
              disabled={isLoading}
              onChange={handleServiceChange}
            />
          </div>
        </div>

        <div className='card-body py-3'>
          <Table>
            <TableHead>
              <tr>
                <th className='min-w-200px'>Название</th>
                <th className='min-w-180px'>Ключ</th>
                <th className='min-w-250px'>Значение</th>
                <th className='min-w-160px'>Дата и кем изменён</th>
              </tr>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <LoaderForTable isLoading colSpan={4} />
              ) : loadError ? (
                <tr>
                  <td colSpan={4} className='text-center text-muted py-10'>
                    {loadError}
                  </td>
                </tr>
              ) : settings.length === 0 ? (
                <LoaderForTable isLoading={false} colSpan={4} />
              ) : (
                Object.entries(groupedSettings).map(([ns, items]) => (
                  <React.Fragment key={ns}>
                    <tr className='bg-light'>
                      <td colSpan={4} className='py-2 px-4'>
                        <span className='fw-bold text-gray-600 fs-7 text-uppercase ls-1'>{ns}</span>
                      </td>
                    </tr>
                    {items.map((s) => (
                      <SystemSettingsRow key={s.key} setting={s} service={currentService} />
                    ))}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Пагинация */}
      {pageCount > 1 && (
        <div className='d-flex justify-content-center mt-5'>
          <ReactPaginate
            breakLabel='...'
            nextLabel='Следующая >'
            onPageChange={({ selected }) => applyFilters({ page: selected + 1 })}
            pageRangeDisplayed={5}
            pageCount={pageCount}
            previousLabel='< Предыдущая'
            renderOnZeroPageCount={() => null}
            className='pagination'
            activeClassName='active'
            disabledClassName='disabled'
            nextClassName='page-item'
            nextLinkClassName='page-link'
            pageClassName='page-item'
            pageLinkClassName='page-link'
            previousClassName='page-item'
            previousLinkClassName='page-link'
            breakClassName='page-item'
            breakLinkClassName='page-link'
          />
        </div>
      )}
    </div>
  )
}
